import React from 'react';
import { TrendingDown, Star, Palette, Award } from 'lucide-react';

const VariantDisplay = ({ listing }) => {
  const { has_variants, variants, price, extraction_method, llm_extraction_time } = listing;

  // If no variants, show single price
  if (!has_variants || !variants || variants.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold text-gray-900">S${price}</span>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {extraction_method === 'llm' ? `LLM ${llm_extraction_time?.toFixed(1)}s` : 'Regex'}
        </span>
      </div>
    );
  }

  // Check if variants have multi-dimensional data (color, condition)
  const hasColorVariants = variants.some(v => v.color);
  const hasConditionVariants = variants.some(v => v.condition);
  const isMultiDimensional = hasColorVariants || hasConditionVariants;

  // Multi-variant display
  const sortedVariants = [...variants].sort((a, b) => a.price - b.price);
  const bestValue = sortedVariants[0];
  const priceRange = {
    min: Math.min(...variants.map(v => v.price)),
    max: Math.max(...variants.map(v => v.price))
  };

  // Helper function to get color hex for visual display
  const getColorHex = (colorName) => {
    const colorMap = {
      'Black': '#000000',
      'White': '#FFFFFF',
      'Blue': '#3B82F6',
      'Red': '#EF4444',
      'Green': '#10B981',
      'Purple': '#A855F7',
      'Pink': '#EC4899',
      'Yellow': '#EAB308',
      'Gold': '#F59E0B',
      'Silver': '#9CA3AF',
      'Gray': '#6B7280',
      'Grey': '#6B7280',
      'Rose': '#F43F5E'
    };
    return colorMap[colorName] || '#9CA3AF';
  };

  // Helper function to get condition badge color
  const getConditionColor = (condition) => {
    if (!condition) return 'bg-gray-100 text-gray-800';
    if (condition.includes('new') || condition.includes('New')) {
      return 'bg-green-100 text-green-800';
    } else if (condition.includes('Lightly')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (condition.includes('Well') || condition.includes('Heavily')) {
      return 'bg-orange-100 text-orange-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Render multi-dimensional variants (with Color + Condition)
  if (isMultiDimensional) {
    return (
      <div className="space-y-3">
        {/* Variant Summary Header */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-semibold">
            🎯 {variants.length} Variant{variants.length > 1 ? 's' : ''} Available
            {hasColorVariants && <span className="ml-2">🎨 Colors</span>}
            {hasConditionVariants && <span className="ml-2">✨ Conditions</span>}
          </span>
        </div>

        {/* Compact Variant List */}
        <div className="space-y-2">
          {sortedVariants.map((variant, index) => {
            const isBestValue = index === 0;
            const variantParts = [];
            if (variant.storage) variantParts.push(variant.storage);
            if (variant.color) variantParts.push(variant.color);
            if (variant.condition) variantParts.push(variant.condition);
            
            return (
              <div 
                key={index} 
                className={`flex items-center justify-between p-2 rounded-lg border-2 ${
                  isBestValue ? 'bg-green-50 border-green-500' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Storage badge */}
                  {variant.storage && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-medium text-xs">
                      {variant.storage}
                    </span>
                  )}
                  
                  {/* Color indicator */}
                  {variant.color && (
                    <span className="px-2 py-1 bg-purple-50 border border-purple-200 rounded font-medium text-xs flex items-center gap-1.5">
                      <span 
                        className="w-3 h-3 rounded-full border border-gray-400" 
                        style={{backgroundColor: getColorHex(variant.color)}}
                      />
                      {variant.color}
                    </span>
                  )}
                  
                  {/* Condition badge */}
                  {variant.condition && (
                    <span className={`px-2 py-1 rounded font-medium text-xs ${getConditionColor(variant.condition)}`}>
                      {variant.condition}
                    </span>
                  )}

                  {/* Best Value Badge */}
                  {isBestValue && (
                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3 fill-white" />
                      Best Value
                    </span>
                  )}
                </div>
                
                {/* Price */}
                <span className={`font-bold text-lg ${isBestValue ? 'text-green-700' : 'text-gray-900'}`}>
                  S${variant.price}
                </span>
              </div>
            );
          })}
        </div>

        {/* Price Range Summary */}
        <div className="flex items-center gap-4 text-sm text-gray-600 pt-2 border-t">
          <span>Price Range: <strong className="text-gray-900">S${priceRange.min} - S${priceRange.max}</strong></span>
        </div>
      </div>
    );
  }

  // Original storage-only variant display (fallback)
  return (
    <div className="space-y-3">
      {/* Variant Summary Header */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="font-semibold">💾 Storage Options ({variants.length} variants):</span>
      </div>

      {/* Variant Grid */}
      <div className="grid grid-cols-3 gap-3">
        {sortedVariants.map((variant, index) => {
          const isBestValue = variant.storage === bestValue.storage;
          const borderColor = isBestValue ? 'border-green-500' : 'border-gray-300';
          const bgColor = isBestValue ? 'bg-green-50' : 'bg-white';
          
          return (
            <div 
              key={variant.storage} 
              className={`border-2 ${borderColor} ${bgColor} rounded-lg p-3 relative`}
            >
              {/* Best Value Badge */}
              {isBestValue && (
                <div className="absolute -top-2 -right-2">
                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" />
                    Best
                  </span>
                </div>
              )}

              {/* Storage Size */}
              <div className="text-xs font-semibold text-gray-700 mb-1">
                {variant.storage}
              </div>

              {/* Price */}
              <div className={`text-xl font-bold ${isBestValue ? 'text-green-700' : 'text-gray-900'}`}>
                S${variant.price}
              </div>

              {/* Extraction Info */}
              <div className="text-xs text-gray-500 mt-1">
                <span className="bg-white px-1 rounded border border-gray-200">
                  {variant.extraction_method === 'llm' ? 'LLM' : 'Regex'}
                </span>
                {variant.llm_extraction_time && (
                  <span className="ml-1">{variant.llm_extraction_time.toFixed(1)}s</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Price Range Summary */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Price Range:</span>
          <span className="font-semibold text-gray-900">
            S${priceRange.min} - S${priceRange.max}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Best Deal:</span>
          <span className="font-semibold text-green-600">
            {bestValue.storage} at S${bestValue.price}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VariantDisplay;
